import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Divider,
  Grid,
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  Quiz,
  Mic,
  Headphones,
  Computer,
  Warning,
  PlayArrow,
} from '@mui/icons-material';
import { apiUtils, API_ENDPOINTS } from '../../config/api';

interface AssignmentData {
  id: string;
  name: string;
  email_id: string;
  assessment_id: string;
  token: string;
  assessment?: {
    name: string;
    configuration: {
      total_duration_minutes: number;
      total_score: number;
      sections: Array<{
        section_name: string;
        duration_minutes?: number;
        total_score?: number;
      }>;
    };
  };
}

interface SystemCheck {
  name: string;
  status: 'checking' | 'passed' | 'failed';
  message: string;
}

const ExamInstructions: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  
  const [assignment, setAssignment] = useState<AssignmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [systemChecks, setSystemChecks] = useState<SystemCheck[]>([
    { name: 'Microphone Access', status: 'checking', message: 'Checking microphone permissions...' },
    { name: 'Audio Playback', status: 'checking', message: 'Testing audio playback...' },
    { name: 'Internet Connection', status: 'checking', message: 'Verifying connection stability...' },
  ]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [startingExam, setStartingExam] = useState(false);

  useEffect(() => {
    if (assignmentId) {
      loadAssignmentData();
      performSystemChecks();
    }
  }, [assignmentId]);

  const loadAssignmentData = async () => {
    try {
      setLoading(true);
      
      // Get assignment details
      const assignmentResponse = await apiUtils.get(`${API_ENDPOINTS.ASSIGNMENTS}/${assignmentId}`);
      
      // Get assessment details
      const assessmentResponse = await apiUtils.get(`${API_ENDPOINTS.ASSESSMENTS}/${assignmentResponse.data.assessment_id}`);
      
      setAssignment({
        ...assignmentResponse.data,
        assessment: assessmentResponse.data
      });
    } catch (err) {
      console.error('Error loading assignment:', err);
      setError('Failed to load assignment details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const performSystemChecks = async () => {
    // Check microphone access
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      updateSystemCheck('Microphone Access', 'passed', 'Microphone access granted');
    } catch (err) {
      updateSystemCheck('Microphone Access', 'failed', 'Microphone access denied. Please allow microphone access.');
    }

    // Check audio playback
    try {
      // Create a simple audio context test instead of playing actual audio
      if (typeof AudioContext !== 'undefined' || typeof (window as any).webkitAudioContext !== 'undefined') {
        const AudioContextClass = AudioContext || (window as any).webkitAudioContext;
        const audioContext = new AudioContextClass();
        
        // Test if we can create audio nodes
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        // Set very low volume to avoid actual sound
        gainNode.gain.setValueAtTime(0.001, audioContext.currentTime);
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        // Start and stop immediately (no audible sound)
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.001);
        
        // Clean up
        audioContext.close();
        
        updateSystemCheck('Audio Playback', 'passed', 'Audio system is working correctly');
      } else {
        updateSystemCheck('Audio Playback', 'failed', 'Audio API not supported in this browser');
      }
    } catch (err) {
      // Fallback: assume audio works if we can't test it
      updateSystemCheck('Audio Playback', 'passed', 'Audio system check completed (unable to verify)');
    }

    // Check internet connection
    try {
      await fetch('/api/v1/health', { method: 'HEAD' });
      updateSystemCheck('Internet Connection', 'passed', 'Internet connection stable');
    } catch (err) {
      updateSystemCheck('Internet Connection', 'failed', 'Internet connection unstable. Please check your connection.');
    }
  };

  const updateSystemCheck = (name: string, status: 'passed' | 'failed', message: string) => {
    setSystemChecks(prev => prev.map(check => 
      check.name === name ? { ...check, status, message } : check
    ));
  };

  const handleStartExam = async () => {
    try {
      setStartingExam(true);
      
      // For now, navigate directly to exam interface
      // In production, this would generate questions first
      navigate(`/exam/${assignmentId}/interface`);
    } catch (err) {
      console.error('Error starting exam:', err);
      setError('Failed to start exam. Please try again.');
    } finally {
      setStartingExam(false);
      setConfirmDialogOpen(false);
    }
  };

  const allSystemChecksPassed = systemChecks.every(check => check.status === 'passed');
  const hasFailedChecks = systemChecks.some(check => check.status === 'failed');

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading exam instructions...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} textAlign="center">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box p={4} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Box maxWidth="800px" mx="auto">
        {/* Header */}
        <Card sx={{ borderRadius: '16px', mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h3" fontWeight={700} gutterBottom>
              📋 Exam Instructions
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              {assignment?.assessment?.name}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.8, mt: 1 }}>
              Student: {assignment?.name} ({assignment?.email_id})
            </Typography>
          </CardContent>
        </Card>

        {/* Assessment Overview */}
        <Card sx={{ borderRadius: '12px', mb: 4 }}>
          <CardContent>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              📊 Assessment Overview
            </Typography>
            
            <Box display="flex" gap={3} sx={{ mt: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <Box flex={1} textAlign="center">
                <Schedule sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  {assignment?.assessment?.configuration.total_duration_minutes} Minutes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Duration
                </Typography>
              </Box>
              <Box flex={1} textAlign="center">
                <Quiz sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  {assignment?.assessment?.configuration.sections?.length || 0} Sections
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Question Sections
                </Typography>
              </Box>
              <Box flex={1} textAlign="center">
                <CheckCircle sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  {assignment?.assessment?.configuration.total_score} Points
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Maximum Score
                </Typography>
              </Box>
            </Box>

            {assignment?.assessment?.configuration.sections && (
              <Box mt={3}>
                <Typography variant="h6" gutterBottom>Sections:</Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {assignment.assessment.configuration.sections.map((section, index) => (
                    <Chip
                      key={index}
                      label={`${section.section_name} (${section.total_score || 0} pts)`}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* System Requirements Check */}
        <Card sx={{ borderRadius: '12px', mb: 4 }}>
          <CardContent>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              🔧 System Requirements Check
            </Typography>
            
            <List>
              {systemChecks.map((check, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    {check.status === 'checking' && <CircularProgress size={24} />}
                    {check.status === 'passed' && <CheckCircle color="success" />}
                    {check.status === 'failed' && <Warning color="error" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={check.name}
                    secondary={check.message}
                  />
                </ListItem>
              ))}
            </List>

            {hasFailedChecks && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Some system checks failed. Please resolve these issues before starting the exam.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Exam Instructions */}
        <Card sx={{ borderRadius: '12px', mb: 4 }}>
          <CardContent>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              📝 Important Instructions
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon><Computer color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Stable Internet Connection"
                  secondary="Ensure you have a stable internet connection throughout the exam."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Mic color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Microphone Access"
                  secondary="Allow microphone access for speaking questions. Test your microphone before starting."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Headphones color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Audio Equipment"
                  secondary="Use headphones or speakers for listening questions. Ensure audio is working properly."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Schedule color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Time Management"
                  secondary="The exam is timed. You cannot pause once started. Plan your time accordingly."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Warning color="warning" /></ListItemIcon>
                <ListItemText
                  primary="No Navigation Away"
                  secondary="Do not close the browser tab or navigate away during the exam. This may result in automatic submission."
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Start Exam Button */}
        <Card sx={{ borderRadius: '12px' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Ready to Start Your Exam?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Once you click "Start Exam", the timer will begin and you cannot pause.
            </Typography>
            
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayArrow />}
              onClick={() => setConfirmDialogOpen(true)}
              disabled={!allSystemChecksPassed}
              sx={{
                py: 2,
                px: 4,
                fontSize: '1.1rem',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                }
              }}
            >
              Start Exam
            </Button>

            {!allSystemChecksPassed && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                Please resolve all system check issues before starting the exam.
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <Warning color="warning" />
              Confirm Exam Start
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Are you sure you want to start the exam? Once started:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• The timer will begin immediately" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• You cannot pause or stop the exam" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Closing the browser may result in automatic submission" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• You have limited time to complete all questions" />
              </ListItem>
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleStartExam}
              disabled={startingExam}
              startIcon={startingExam ? <CircularProgress size={20} /> : <PlayArrow />}
            >
              {startingExam ? 'Starting...' : 'Start Exam'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ExamInstructions;
